// src/pages/Coleta/components/HistoricoQualitativo.jsx
import React from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    MenuItem,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import * as Lucide from 'lucide-react';
import { rotuloTurno, situacaoColeta } from '../../../utils/coleta';
import SerialDispositivo from './SerialDispositivo';

const dois = (n) => String(n).padStart(2, '0');
const diaMes = (d) => `${dois(d.getDate())}/${dois(d.getMonth() + 1)}`;
const horaMin = (d) => `${dois(d.getHours())}:${dois(d.getMinutes())}`;

/**
 * Resultado de um alvo na linha: detectado / não detectado depois de processado; antes
 * disso, em que pé a amostra está (só na primeira coluna, para não repetir).
 */
function ResultadoAlvo({ linha, alvo, primeira, processando, podeProcessar, onProcessar }) {
    const exp = linha.experimento;
    const estado = (texto, cor = 'text.secondary') => (primeira ? <Typography variant="body2" color={cor}>{texto}</Typography> : null);
    if (!exp) return estado('Aguardando tubo');
    if (exp.excluido) return estado('Ensaio excluído', 'error.main');
    if (processando) {
        return primeira ? (
            <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={14} />
                <Typography variant="body2" color="text.secondary">Processando…</Typography>
            </Stack>
        ) : null;
    }
    if (!exp.processado) {
        if (String(exp.status).toLowerCase() === 'active') return estado('Em análise');
        if (!primeira || !podeProcessar) return estado('Não processada');
        return (
            <Button size="small" variant="contained" disableElevation onClick={() => onProcessar(linha)} sx={{ borderRadius: 2 }}>
                Processar
            </Button>
        );
    }
    const resultado = (exp.alvos || []).find((a) => a.alvo === alvo);
    const presenca = resultado ? resultado.presenca : exp.ausente ? false : null;
    return (
        <Box>
            <Typography fontWeight={700}>
                {presenca === true ? 'Detectado' : presenca === false ? 'Não detectado' : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">Resultado qualitativo</Typography>
        </Box>
    );
}

function Amostra({ linha, nomePonto, Icone, cor }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box sx={{ p: 0.9, borderRadius: 2, bgcolor: alpha(cor, 0.12), color: cor, display: 'flex', flexShrink: 0 }}>
                <Icone size={18} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700}>{linha.identificacao}</Typography>
                {nomePonto(linha.coleta?.pontoColetaId) && (
                    <Typography variant="body2" color="text.secondary">{nomePonto(linha.coleta?.pontoColetaId)}</Typography>
                )}
                {linha.coleta?.origem && (
                    <Typography variant="body2" color="text.secondary">{linha.coleta.origem}</Typography>
                )}
            </Box>
        </Stack>
    );
}

function Coleta({ coleta }) {
    const d = coleta?.dataHora ? new Date(coleta.dataHora) : null;
    if (!d || Number.isNaN(d.getTime())) return <Typography variant="body2">—</Typography>;
    return (
        <Box>
            <Typography variant="body2" fontWeight={600}>{diaMes(d)}</Typography>
            <Typography variant="caption" color="text.secondary">
                {horaMin(d)}{coleta.turno ? ` · ${rotuloTurno(coleta.turno)}` : ''}
            </Typography>
        </Box>
    );
}

function Situacao({ status }) {
    const s = situacaoColeta(status);
    return (
        <Chip
            size="small"
            variant="outlined"
            color={s.cor === 'default' ? undefined : s.cor}
            icon={s.chave === 'concluida' ? <Lucide.CircleCheck size={14} /> : undefined}
            label={s.rotulo}
            sx={{ fontWeight: 600 }}
        />
    );
}

/**
 * Histórico da página qualitativa (água): uma linha por amostra, com detectado / não
 * detectado de cada alvo (E. coli e coliformes totais vêm do MESMO teste).
 */
export default function HistoricoQualitativo({
    linhas,
    total,
    page,
    totalPages,
    onPageChange,
    tamanho,
    tamanhos,
    onTamanhoChange,
    carregando,
    alvos,
    Icone,
    cor,
    nomePonto,
    processando,
    podeProcessar,
    onProcessar,
    onAbrir,
}) {
    const theme = useTheme();
    // Cartoes ate 1200px: com a barra lateral aberta, a tabela (7+ colunas) nao cabe
    // no tablet/notebook pequeno e as colunas da direita ficavam escondidas.
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const inicio = total === 0 ? 0 : page * tamanho + 1;
    const fim = Math.min(total, (page + 1) * tamanho);
    const props = (linha) => ({ linha, processando: processando.has(linha.id), podeProcessar, onProcessar });

    return (
        <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                <Typography fontWeight={700} sx={{ fontSize: '1.1rem' }}>Análises de água</Typography>
                <Typography variant="body2" color="text.secondary">
                    {total === 1 ? '1 amostra encontrada' : `${total} amostras encontradas`}
                </Typography>
            </Box>

            {carregando && linhas.length === 0 ? (
                <Box sx={{ px: 3, pb: 3 }}>
                    <Skeleton variant="rounded" height={160} />
                </Box>
            ) : linhas.length === 0 ? (
                <Typography color="text.secondary" sx={{ px: 3, pb: 4 }}>Nenhuma análise de água ainda.</Typography>
            ) : isMobile ? (
                <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} sx={{ borderTop: 1, borderColor: 'divider' }}>
                    {linhas.map((linha) => (
                        <Box key={linha.id} sx={{ p: 2 }} onClick={() => onAbrir(linha)}>
                            <Amostra linha={linha} nomePonto={nomePonto} Icone={Icone} cor={cor} />
                            <Box sx={{ mt: 1 }}><Situacao status={linha.status} /></Box>
                            <Box sx={{ mt: 1 }}><SerialDispositivo serial={linha.numeroSerie} compacto /></Box>
                            <Box sx={{ mt: 1.5 }}><Coleta coleta={linha.coleta} /></Box>
                            <Stack direction="row" spacing={3} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                                {alvos.map((a, i) => (
                                    <Box key={a.alvo}>
                                        <Typography variant="caption" color="text.secondary">{a.rotulo}</Typography>
                                        <ResultadoAlvo alvo={a.alvo} primeira={i === 0} {...props(linha)} />
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 860 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>Amostra / origem</TableCell>
                            <TableCell>Coleta</TableCell>
                            <TableCell>Dispositivo</TableCell>
                            {alvos.map((a) => (
                                <TableCell key={a.alvo}>{a.rotulo}</TableCell>
                            ))}
                            <TableCell>Status</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {linhas.map((linha) => (
                            <TableRow key={linha.id} hover>
                                <TableCell><Amostra linha={linha} nomePonto={nomePonto} Icone={Icone} cor={cor} /></TableCell>
                                <TableCell><Coleta coleta={linha.coleta} /></TableCell>
                                <TableCell><SerialDispositivo serial={linha.numeroSerie} /></TableCell>
                                {alvos.map((a, i) => (
                                    <TableCell key={a.alvo}>
                                        <ResultadoAlvo alvo={a.alvo} primeira={i === 0} {...props(linha)} />
                                    </TableCell>
                                ))}
                                <TableCell><Situacao status={linha.status} /></TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Ver amostra">
                                        <IconButton onClick={() => onAbrir(linha)} aria-label={`Ver amostra ${linha.identificacao}`}>
                                            <Lucide.Eye size={18} />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                </Box>
            )}

            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">{inicio}–{fim} de {total} amostras</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                        select
                        size="small"
                        value={tamanho}
                        onChange={(e) => onTamanhoChange(Number(e.target.value))}
                        inputProps={{ 'aria-label': 'Amostras por página' }}
                        sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                    >
                        {tamanhos.map((n) => (
                            <MenuItem key={n} value={n}>{n}</MenuItem>
                        ))}
                    </TextField>
                    <IconButton size="small" disabled={page === 0} onClick={() => onPageChange(page - 1)} aria-label="Página anterior">
                        <Lucide.ChevronLeft size={18} />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary">{totalPages === 0 ? 0 : page + 1} / {totalPages}</Typography>
                    <IconButton size="small" disabled={page + 1 >= totalPages} onClick={() => onPageChange(page + 1)} aria-label="Próxima página">
                        <Lucide.ChevronRight size={18} />
                    </IconButton>
                </Stack>
            </Stack>
        </Paper>
    );
}
